// The Inbox's rules without React (Inbox.tsx): what each reading holds and waits for, the change a decision or
// an edit saves, and what Add to the strategy would add now (shared applyReading, the one the API runs).
import {
  applyReading,
  elementsForReading,
  ELEMENT_EDITS,
  inboxKey,
  LINK_EDITS,
  normalizeContent,
  reviewed,
  SOURCE_EDITS,
  type Decision,
  type InboxDecision,
  type ProposedSource,
  type ReadingInfo,
  type Reviewed,
  type StrategyChange,
  type StrategyState,
} from '@pathonline/shared';
import type { Lang } from './i18n.ts';

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);

export interface ReadingView {
  info: ReadingInfo;
  r: Reviewed;
  counts: { waiting: number; accepted: number; rejected: number; added: number };
  /** What Add to the strategy would add now. */
  toAdd: { elements: number; links: number; same: number };
  problems: string[];
  /**
   * Read, nothing waits for a decision, and nothing accepted can still be added (a link whose other end was
   * rejected never can). Accepted ones held back by a problem (no institution chosen) keep it open.
   */
  done: boolean;
}

/** Each reading as the Inbox shows it, newest first (as the API lists them). */
/** The query key of GET /api/strategy/readings (StrategyPage readingsQuery): asked again where a document joins or leaves the queue. */
export const READINGS_KEY = ['strategy-readings'];

export function readingViews(readings: readonly ReadingInfo[], content: Obj | null, state: StrategyState): ReadingView[] {
  const base = normalizeContent(content ?? {});
  // A failed one with a newer reading of its document was read again.
  const latest = latestByDoc(readings);
  return readings.map((info) => {
    const again = latest.get(info.doc) !== info;
    const r = reviewed(info, state.inbox);
    const all = [...r.elements, ...r.links];
    const counts = {
      waiting: all.filter((x) => x.s === 'pending').length,
      accepted: all.filter((x) => x.s === 'accepted' && !x.added).length,
      rejected: all.filter((x) => x.s === 'rejected').length,
      added: all.filter((x) => x.added).length,
    };
    const a = info.status === 'ready' && counts.accepted ? applyReading(base, state, info, state.inbox) : null;
    const toAdd = a?.counts ?? { elements: 0, links: 0, same: 0 };
    const problems = a?.problems ?? [];
    const addable = toAdd.elements + toAdd.links + toAdd.same > 0 || (counts.accepted > 0 && problems.length > 0);
    return { info, r, counts, toAdd, problems, done: (info.status === 'ready' && !counts.waiting && !addable) || (info.status === 'failed' && again) };
  });
}

/** Proposals waiting for a decision, over every reading: the Inbox tab's number. */
export const waitingOf = (views: readonly ReadingView[]): number => views.reduce((n, v) => n + v.counts.waiting, 0);

const entry = (inbox: Obj, key: string): InboxDecision => (isObj(inbox[key]) ? (inbox[key] as InboxDecision) : {});

/** A proposal accepted, rejected, or back to waiting (null): its edits stay. */
export function decisionChange(id: string, inbox: Obj, ref: string, s: Decision | null): StrategyChange {
  const key = inboxKey(id, ref);
  const { s: _was, ...rest } = entry(inbox, key);
  const value = s ? { ...rest, s } : rest;
  return { section: 'inbox', key, value: Object.keys(value).length ? value : null };
}

/**
 * A proposal's edits: the fields that differ from what Claude proposed, so changing a field back undoes it.
 * Editing a waiting proposal accepts it: an edit is a decision to keep it.
 */
export function editChange(info: Pick<ReadingInfo, 'id' | 'reading'>, inbox: Obj, ref: string, values: Record<string, unknown>): StrategyChange {
  const key = inboxKey(info.id, ref);
  const original = (ref.startsWith('E') ? info.reading?.elements : info.reading?.links)?.find((x) => x.ref === ref) as Obj | undefined;
  const allowed: readonly string[] = ref.startsWith('E') ? ELEMENT_EDITS : LINK_EDITS;
  const edit: Obj = {};
  for (const [k, v] of Object.entries(values)) if (allowed.includes(k) && v !== undefined && v !== original?.[k]) edit[k] = v;
  const was = entry(inbox, key);
  const value: Obj = { s: was.s ?? 'accepted', ...(Object.keys(edit).length ? { edit } : {}) };
  return { section: 'inbox', key, value };
}

/** The document's institution and source, as changed: the fields that differ from Claude's. */
export function sourceChange(info: Pick<ReadingInfo, 'id' | 'reading'>, values: Partial<ProposedSource>): StrategyChange {
  const original = (info.reading?.source ?? {}) as unknown as Obj;
  const edit: Obj = {};
  for (const [k, v] of Object.entries(values)) if ((SOURCE_EDITS as readonly string[]).includes(k) && typeof v === 'string' && v !== original[k]) edit[k] = v.trim();
  return { section: 'inbox', key: inboxKey(info.id), value: Object.keys(edit).length ? edit : null };
}

/** Every element's title in the language shown, by id: how a link's other end or a parent is named. */
export function titlesOf(content: Obj | null, state: StrategyState, lang: Lang): Map<string, string> {
  return new Map(elementsForReading(content ?? {}, state).map((e) => [e.id, (lang === 'en' ? e.titleEn : '') || e.title || e.titleEn]));
}

/** The newest reading of each document (by its library key). */
export function latestByDoc(readings: readonly ReadingInfo[]): Map<string, ReadingInfo> {
  const out = new Map<string, ReadingInfo>();
  for (const r of readings) if (!out.has(r.doc)) out.set(r.doc, r);
  return out;
}
